import React, { useState, useEffect } from 'react';
import { 
  Bell, 
  Smartphone, 
  Mail, 
  MessageSquare, 
  Heart, 
  Video, 
  CreditCard, 
  Shield, 
  TrendingUp,
  Clock,
  Volume2,
  VolumeX,
  Settings
} from 'lucide-react';

const NotificationPreferences = () => {
  const [preferences, setPreferences] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [doNotDisturb, setDoNotDisturb] = useState({
    enabled: false,
    startTime: '22:00',
    endTime: '08:00'
  });

  useEffect(() => {
    loadPreferences();
    checkPushPermission();
  }, []);

  const loadPreferences = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/preferences`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPreferences(data.preferences);
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkPushPermission = () => {
    if ('Notification' in window) {
      setPushEnabled(Notification.permission === 'granted');
    }
  };

  const savePreferences = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/preferences`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          preferences: preferences,
          quiet_hours_start: doNotDisturb.enabled ? doNotDisturb.startTime : null,
          quiet_hours_end: doNotDisturb.enabled ? doNotDisturb.endTime : null
        })
      });

      if (response.ok) {
        alert('Notification preferences saved successfully!');
      } else {
        alert('Failed to save preferences. Please try again.');
      }
    } catch (error) {
      console.error('Error saving preferences:', error);
      alert('An error occurred while saving preferences.');
    } finally {
      setSaving(false);
    }
  };

  const requestPushPermission = async () => {
    if (!('Notification' in window)) {
      alert('This browser does not support notifications');
      return;
    }

    try {
      const permission = await Notification.requestPermission();
      setPushEnabled(permission === 'granted');
      
      if (permission === 'granted') {
        // Subscribe to push notifications
        await subscribeToPush();
      }
    } catch (error) {
      console.error('Error requesting push permission:', error);
    }
  };

  const subscribeToPush = async () => {
    try {
      // This would integrate with service worker for push notifications
      // For now, we'll just update the preference
      console.log('Push subscription would be set up here');
    } catch (error) {
      console.error('Error subscribing to push:', error);
    }
  };

  const updatePreference = (notificationType, channel, enabled) => {
    setPreferences(prev => ({
      ...prev,
      [notificationType]: {
        ...prev[notificationType],
        channels: enabled 
          ? [...(prev[notificationType]?.channels || []), channel].filter((item, index, arr) => arr.indexOf(item) === index)
          : (prev[notificationType]?.channels || []).filter(ch => ch !== channel),
        enabled: prev[notificationType]?.enabled !== false // Keep enabled unless explicitly disabled
      }
    }));
  };

  const toggleNotificationType = (notificationType, enabled) => {
    setPreferences(prev => ({
      ...prev,
      [notificationType]: {
        ...prev[notificationType],
        enabled: enabled,
        channels: enabled ? ['in_app', 'push'] : []
      }
    }));
  };

  const notificationTypes = [
    {
      id: 'match_new',
      title: 'New Matches',
      description: 'When someone likes you back',
      icon: Heart,
      color: 'text-red-500',
      category: 'Matches'
    },
    {
      id: 'match_mutual',
      title: 'Mutual Matches',
      description: 'When you have a mutual like',
      icon: Heart,
      color: 'text-red-500',
      category: 'Matches'
    },
    {
      id: 'message_new',
      title: 'New Messages',
      description: 'When someone sends you a message',
      icon: MessageSquare,
      color: 'text-blue-500',
      category: 'Messages'
    },
    {
      id: 'video_call_incoming',
      title: 'Incoming Video Calls',
      description: 'When someone wants to start a speed date',
      icon: Video,
      color: 'text-purple-500',
      category: 'Video Calls'
    },
    {
      id: 'payment_failed',
      title: 'Payment Issues',
      description: 'When there are payment problems',
      icon: CreditCard,
      color: 'text-red-500',
      category: 'Payments'
    },
    {
      id: 'profile_verified',
      title: 'Profile Verification',
      description: 'When your profile gets verified',
      icon: Shield,
      color: 'text-green-500',
      category: 'Safety'
    }
  ];

  const channels = [
    { id: 'in_app', label: 'In-App', icon: Bell, description: 'Show in notification center' },
    { id: 'push', label: 'Push', icon: Smartphone, description: 'Browser push notifications' },
    { id: 'email', label: 'Email', icon: Mail, description: 'Send to your email' }
  ];

  const groupedNotifications = notificationTypes.reduce((groups, notification) => {
    const category = notification.category;
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(notification);
    return groups;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-400 via-pink-500 to-purple-600 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-400 via-pink-500 to-purple-600 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">Notification Settings</h1>
          <p className="text-white/80">Customize how you want to be notified</p>
        </div>

        {/* Push Permission */}
        {!pushEnabled && (
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 mb-6 border border-yellow-300/30">
            <div className="flex items-center space-x-4">
              <div className="bg-yellow-500/20 p-3 rounded-full">
                <Bell className="h-6 w-6 text-yellow-300" />
              </div>
              <div className="flex-1">
                <h3 className="text-white font-semibold mb-1">Enable Push Notifications</h3>
                <p className="text-white/80 text-sm">Get notified even when you're not using the app</p>
              </div>
              <button
                onClick={requestPushPermission}
                className="bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-full font-semibold transition-colors"
              >
                Enable
              </button>
            </div>
          </div>
        )}

        {/* Notification Categories */}
        <div className="space-y-6">
          {Object.entries(groupedNotifications).map(([category, notifications]) => (
            <div key={category} className="bg-white/10 backdrop-blur-md rounded-2xl p-6">
              <h2 className="text-xl font-semibold text-white mb-4">{category}</h2>
              
              <div className="space-y-4">
                {notifications.map((notification) => (
                  <div key={notification.id} className="bg-white/10 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <notification.icon className={`h-5 w-5 ${notification.color}`} />
                        <div>
                          <h4 className="text-white font-medium">{notification.title}</h4>
                          <p className="text-white/70 text-sm">{notification.description}</p>
                        </div>
                      </div>
                      
                      {/* Master toggle */}
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={preferences[notification.id]?.enabled !== false}
                          onChange={(e) => toggleNotificationType(notification.id, e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-rose-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-rose-600"></div>
                      </label>
                    </div>

                    {/* Channel toggles */}
                    {preferences[notification.id]?.enabled !== false && (
                      <div className="grid grid-cols-3 gap-3">
                        {channels.map((channel) => (
                          <label key={channel.id} className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={(preferences[notification.id]?.channels || []).includes(channel.id)}
                              onChange={(e) => updatePreference(notification.id, channel.id, e.target.checked)}
                              className="w-4 h-4 text-rose-600 bg-transparent border-gray-300 rounded focus:ring-rose-500 focus:ring-2"
                            />
                            <div className="flex items-center space-x-1">
                              <channel.icon className="h-4 w-4 text-white/60" />
                              <span className="text-white/80 text-sm">{channel.label}</span>
                            </div>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Save Button */}
        <div className="mt-8 text-center">
          <button
            onClick={savePreferences}
            disabled={saving}
            className="bg-rose-500 hover:bg-rose-600 disabled:bg-rose-300 text-white px-8 py-3 rounded-full text-lg font-semibold transition-colors"
          >
            {saving ? 'Saving...' : 'Save Preferences'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationPreferences;